import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { CancelIcon } from '../../../ui';
import {
	CloseButtonLabel,
	Container,
	Dialog,
	DialogScroll,
	DialogContent,
	SearchInputWrapper,
	SearchCloseButton,
	SearchContainer,
	SearchWrapper,
} from './SearchOverlay.styles';
import { SearchInputContainer } from './SearchInputContainer';
import { ContentSearchStore } from '../domain/stores/createContentSearchStore';
import { SearchOverlayContent } from './SearchOverlayContent';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape';
import { useSearchData } from '../../../common-data/useSearchData';
import { getReciteMeBarHeight } from '../../../utils/getReciteMeBarHeight';
import { PoiStore } from '../../shop-dine-relax/search/domain/hooks/usePoiStore';
import { FlightListStore } from '../../flights/overview/domain/stores/FlightListStore';

interface ISearchOverlayDialogProps {
	flightStore: FlightListStore;
	locationStore: PoiStore;
	informationStore: ContentSearchStore;
	query: string;
	updateQuery: (newQuery: string) => void;
	searchInputRef: React.RefObject<HTMLInputElement>;
	isDebouncing: boolean;
}

export const SearchOverlayDialog = observer(
	({
		flightStore,
		locationStore,
		informationStore,
		query,
		updateQuery,
		searchInputRef,
		isDebouncing,
	}: ISearchOverlayDialogProps) => {
		const { isSearchOpen, updateSearchOpen } = useSearchData();

		const [searchOffset, setSearchOffset] = useState(0);

		useEffect(() => {
			window.addEventListener('resize', checkHeaderHeight);
			checkHeaderHeight();
			return () => window.removeEventListener('resize', checkHeaderHeight);
		}, []);

		// adjust the search bar height based on the sticky header height
		const checkHeaderHeight = () => {
			const header = document.getElementById('sticky-wrapper');
			const headerHeight = header?.getBoundingClientRect().height;
			setSearchOffset(headerHeight || 225);
		};

		const onClose = () => updateSearchOpen(false);
		useCloseOnEscape(isSearchOpen, onClose);

		const stickyTop = getReciteMeBarHeight();
		const scrollRef = useRef<HTMLDivElement>(null);

		return (
			<Dialog isOpen={isSearchOpen} id="search-dialog" stickyTop={stickyTop}>
				<Container>
					<SearchWrapper>
						<SearchContainer
							direction="column"
							alignItems="center"
							justifyContent="flex-end"
							gap="16"
							searchOffset={searchOffset}
						>
							<SearchInputWrapper direction="row" alignItems="center">
								<SearchInputContainer
									query={query}
									updateQuery={updateQuery}
									inputRef={searchInputRef}
								/>
								<SearchCloseButton
									inverted
									buttonType="textButton"
									icon={<CancelIcon />}
									iconPosition="right"
									onClick={onClose}
								>
									<CloseButtonLabel>Close</CloseButtonLabel>
								</SearchCloseButton>
							</SearchInputWrapper>
						</SearchContainer>
					</SearchWrapper>
					<DialogScroll ref={scrollRef}>
						<DialogContent direction="column" gap="16">
							<SearchOverlayContent
								flightStore={flightStore}
								locationStore={locationStore}
								informationStore={informationStore}
								isDebouncing={isDebouncing}
								query={query}
								scrollRef={scrollRef}
								updateQuery={updateQuery}
							/>
						</DialogContent>
					</DialogScroll>
				</Container>
			</Dialog>
		);
	},
);
